import { IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, Chip, Stack } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { HallMaintenanceTask } from '../../domain/HallMaintenanceTask';
import { alpha } from '@mui/material/styles';

interface Props {
    tasks: HallMaintenanceTask[];
    canManage: boolean;
    onView: (task: HallMaintenanceTask) => void;
    onEdit: (task: HallMaintenanceTask) => void;
    onDelete: (task: HallMaintenanceTask) => void;
    onAssignments: (task: HallMaintenanceTask) => void;
}

const HallMaintenanceTasksDesktopTable = (props: Props) => {
    const { t } = useTranslation();
    const monthNames = moment.monthsShort();
    const currentMonth = moment().month() + 1;

    const formatFrequency = (task: HallMaintenanceTask) => {
        if (task.frequencyLabel) {
            return task.frequencyLabel;
        }
        const interval = task.frequencyInterval || 1;
        const unitLabel = t(task.frequencyUnit);
        return `${interval} ${unitLabel}`;
    };


    const isPlannedForCurrentMonth = (task: HallMaintenanceTask) => {
        return task.plannedMonths?.includes(currentMonth);
    };

    return (
        <TableContainer component={Paper}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{t("card_number")}</TableCell>
                        <TableCell>{t("title")}</TableCell>
                        <TableCell>{t("category")}</TableCell>
                        <TableCell>{t("frequency")}</TableCell>
                        <TableCell>{t("planned_months")}</TableCell>
                        <TableCell align="right">{t("actions")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {props.tasks.map((task) => (
                        <TableRow
                            key={task.id}
                            hover
                            sx={(theme) => ({
                                cursor: 'pointer',
                                backgroundColor: isPlannedForCurrentMonth(task)
                                    ? alpha(theme.palette.success.main, 0.08)
                                    : undefined,
                                '&:hover': isPlannedForCurrentMonth(task)
                                    ? { backgroundColor: alpha(theme.palette.success.main, 0.14) }
                                    : undefined,
                            })}
                            onClick={() => props.onView(task)}
                        >
                            <TableCell>
                                {task.cardNumber ? (
                                    <Typography variant="subtitle2">{task.cardNumber}</Typography>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">{t("no_data")}</Typography>
                                )}
                            </TableCell>
                            <TableCell>
                                <Typography variant="subtitle1" fontWeight={600}>{task.title}</Typography>
                                {task.subTitle && (
                                    <Typography variant="body2" color="text.secondary">
                                        {task.subTitle}
                                    </Typography>
                                )}
                            </TableCell>
                            <TableCell>{t(task.category)}</TableCell>
                            <TableCell>{formatFrequency(task)}</TableCell>
                            <TableCell>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                    {task.plannedMonths && task.plannedMonths.length > 0 ? (
                                        task.plannedMonths.sort((a, b) => a - b).map(month => (
                                            <Chip
                                                key={`${task.id}-${month}`}
                                                size="small"
                                                label={monthNames[(month - 1 + 12) % 12]}
                                                color={month === currentMonth ? 'success' : 'default'}
                                                variant={month === currentMonth ? 'outlined' : 'filled'}
                                                sx={{ mb: 0.5, fontWeight: month === currentMonth ? 600 : undefined }}
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">{t("no_data")}</Typography>
                                    )}
                                </Stack>
                            </TableCell>
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                <Tooltip title={t("task_assignments")}>
                                    <IconButton onClick={() => props.onAssignments(task)}>
                                        <AssignmentIndIcon color="action" />
                                    </IconButton>
                                </Tooltip>
                                {props.canManage && (
                                    <>
                                        <Tooltip title={t("edit")}>
                                            <IconButton onClick={() => props.onEdit(task)}>
                                                <EditIcon color="primary" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t("delete")}>
                                            <IconButton onClick={() => props.onDelete(task)}>
                                                <DeleteIcon color="error" />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                    {props.tasks.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={6}>
                                <Typography variant="body2" color="text.secondary">{t("no_data")}</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default HallMaintenanceTasksDesktopTable;
